import {
	useGetInventoryLocationsQuery,
	useLazyGetInventoryLocationsQuery,
	useSplitMaterialLengthMutation,
} from '../../redux/api/material.ts';
import { Button, Form, InputNumber, Modal, Select, Typography, Row, Col, Card, Tag, Tooltip, message } from 'antd';
import { DeleteOutlined, PlusOutlined, CopyOutlined, VerticalAlignBottomOutlined } from '@ant-design/icons';
import { Material, MaterialLength } from '../../interfaces/MaterialInventory.interface';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import AttributeCostJobField from './AttributeCostJobField';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import MaterialLocationsSummaryModal from '../Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import { toNum, barFitsInLocation, hasNoAvailability, renderLocationOption, filterLocationOption, NO_LOCATION_VALUE, NO_LOCATION_LABEL } from '../MaterialLengths/materialLengthLocationUtils';
import BuildingTag from '../BuildingTag';
import OperatorPinModal from '../Operators/OperatorPinModal';
import { locationSearchText, useLocationLabel } from '../../utils/locationLabel';

const { Text, Title } = Typography;

const DEBOUNCE_MS = 400;
type LocationOption = { value: string; label: string; disabled?: boolean; isCurrent?: boolean; searchText?: string };

interface SplitLengthModalProps {
	isSplitLengthModalOpen: boolean;
	onClose: () => void;
	material: Material;
	selectedMaterialLength: MaterialLength | null;
	refetchData: () => void;
}

const SplitLengthModal = ({
	isSplitLengthModalOpen,
	onClose,
	material,
	selectedMaterialLength,
	refetchData,
}: SplitLengthModalProps) => {
	const [form] = Form.useForm();
	const buildingName = selectedMaterialLength?.inventory_location?.shelf?.rack?.building?.name;
	const formatLocation = useLocationLabel();
	const locLabel = (loc: any, fallback: string) =>
		formatLocation(loc?.display_name) || loc?.code || fallback;

	// Debounced main bar new length for location API (same pattern as ModifyLengthsModal)
	const mainNewLengthFromForm = Form.useWatch('main_new_length', form);
	const [debouncedMainNewLength, setDebouncedMainNewLength] = useState<number | null>(null);
	useEffect(() => {
		const raw = mainNewLengthFromForm != null ? Number(mainNewLengthFromForm) : null;
		const t = setTimeout(() => setDebouncedMainNewLength(Number.isFinite(raw) ? raw : null), DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [mainNewLengthFromForm]);

	// Main bar: same API as ModifyLengthsModal — getInventoryLocations with length_overrides
	const mainBarId = selectedMaterialLength?.id ? String(selectedMaterialLength.id) : null;
	const mainBarLengthOverride = debouncedMainNewLength != null && Number.isFinite(debouncedMainNewLength)
		? debouncedMainNewLength
		: selectedMaterialLength?.current_length != null
			? Number(selectedMaterialLength.current_length)
			: null;
	const buildingId = selectedMaterialLength?.inventory_location?.shelf?.rack?.building?.id;

	const mainBarQueryParams = useMemo(() => {
		if (!mainBarId || mainBarLengthOverride == null) return null;
		return {
			material_length_ids: [mainBarId],
			length_overrides: JSON.stringify({ [mainBarId]: mainBarLengthOverride }),
			...(buildingId && { building: buildingId }),
		};
	}, [mainBarId, mainBarLengthOverride, buildingId]);

	const { data: mainLocationsData, isLoading: mainLocationsLoading, isFetching: mainLocationsFetching } =
		useGetInventoryLocationsQuery(mainBarQueryParams ?? { material_length_ids: [] }, {
			skip: !isSplitLengthModalOpen || !mainBarId || mainBarQueryParams == null,
			refetchOnMountOrArgChange: true,
		});

	const mainLocationsRequesting = mainLocationsLoading || mainLocationsFetching;

	// Build main bar options using same utils as ModifyLengthsModal (fit + availability)
	const mainLocationOptions = useMemo((): LocationOption[] => {
		// Consumed original bar (New Length = 0): it holds no physical location, so offer
		// only "No Location Assigned" (maps to a null inventory_location on submit).
		if (mainNewLengthFromForm != null && Number(mainNewLengthFromForm) === 0) {
			return [{ value: NO_LOCATION_VALUE, label: NO_LOCATION_LABEL }];
		}
		const entry = (mainLocationsData?.by_material_length ?? []).find(
			(e: any) => String(e.material_length_id) === mainBarId
		);
		if (!entry?.locations?.length) return [];
		const consumption = entry.consumption
			? {
				weight_per_bar: toNum(entry.consumption.weight_per_bar),
				volume_per_bar: toNum(entry.consumption.volume_per_bar),
				width_per_bar: toNum(entry.consumption.width_per_bar),
				height_per_bar: toNum(entry.consumption.height_per_bar),
			}
			: { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
		const options: LocationOption[] = [];
		(entry.locations ?? []).forEach((loc: any) => {
			if (loc == null) return;
			const locId = String(loc.id);
			const baseAvailW = toNum(loc.available_weight);
			const baseAvailV = toNum(loc.available_volume);
			const baseAvailWd = toNum(loc.available_width);
			const baseAvailH = toNum(loc.available_height);
			const effectiveAvailable = {
				weight: baseAvailW,
				volume: baseAvailV,
				width: baseAvailWd,
				height: baseAvailH,
				bars: toNum(loc.available_bars),
			};
			const fits = barFitsInLocation(loc, consumption, effectiveAvailable);
			const noAvailability = hasNoAvailability(loc.type ?? '', effectiveAvailable, loc);
			options.push({
				value: locId,
				label: locLabel(loc, locId),
				searchText: locationSearchText(loc?.display_name, formatLocation) || undefined,
				disabled: !fits || noAvailability,
				isCurrent: Boolean(loc.is_current_location),
			});
		});
		return options;
	}, [mainLocationsData?.by_material_length, mainBarId, mainNewLengthFromForm, formatLocation]);

	const mainLocationOptionValueSet = useMemo(
		() => new Set(mainLocationOptions.map((o) => o.value)),
		[mainLocationOptions]
	);

	// When main bar new length is unchanged from current, disable location but still show current location if assigned
	const mainNewLengthForm = Form.useWatch('main_new_length', form);
	const hasMainLengthChanged = useMemo(() => {
		if (selectedMaterialLength?.current_length == null || mainNewLengthForm == null) return false;
		const current = Number(selectedMaterialLength.current_length);
		const newLen = Number(mainNewLengthForm);
		return Number.isFinite(newLen) && Number.isFinite(current) && newLen !== current;
	}, [selectedMaterialLength?.current_length, mainNewLengthForm]);

	const mainBarCurrentLocId =
		selectedMaterialLength?.inventory_location?.id ??
		selectedMaterialLength?.inventory_location ??
		selectedMaterialLength?.material_shelf?.id ??
		selectedMaterialLength?.material_shelf;
	const mainBarCurrentLocIdStr =
		mainBarCurrentLocId != null && mainBarCurrentLocId !== '' ? String(mainBarCurrentLocId) : null;
	const mainBarCurrentLocationLabel = useMemo(() => {
		if (!mainBarCurrentLocIdStr) return null;
		const loc = selectedMaterialLength?.inventory_location;
		const locationName =
			(loc != null && typeof loc === 'object' && (loc.display_name || loc.code)) ||
			(selectedMaterialLength as any)?.inventory_location_display_name;
		const label = locationName
			? formatLocation(locationName)
			: (selectedMaterialLength as any)?.material_shelf_display_name;
		if (label && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(label)))
			return String(label);
		return 'Location';
	}, [mainBarCurrentLocIdStr, selectedMaterialLength, formatLocation]);

	// Clear main_location only when we have candidates and current selection is not in the list (same as ModifyLengthsModal)
	useEffect(() => {
		if (!isSplitLengthModalOpen || !selectedMaterialLength || mainLocationOptionValueSet.size === 0) return;
		const currentMain = form.getFieldValue('main_location');
		const key = currentMain != null && currentMain !== '' ? String(currentMain) : null;
		if (!key || mainLocationOptionValueSet.has(key)) return;
		form.setFieldsValue({ main_location: undefined });
	}, [isSplitLengthModalOpen, selectedMaterialLength, mainLocationOptionValueSet, form]);

	// Consumed original bar (New Length = 0): auto-select "No Location Assigned" (the only valid option)
	useEffect(() => {
		if (!isSplitLengthModalOpen) return;
		const consumed = mainNewLengthFromForm != null && Number(mainNewLengthFromForm) === 0;
		if (consumed && form.getFieldValue('main_location') !== NO_LOCATION_VALUE) {
			form.setFieldsValue({ main_location: NO_LOCATION_VALUE });
		}
	}, [isSplitLengthModalOpen, mainNewLengthFromForm, form]);

	// New bars: same getInventoryLocations API via lazy query, one request per distinct length
	const [fetchInventoryLocations] = useLazyGetInventoryLocationsQuery();
	const [newBarLocationOptionsMap, setNewBarLocationOptionsMap] = useState<Record<string, LocationOption[]>>({});
	const [newBarLocationsLoading, setNewBarLocationsLoading] = useState(false);
	const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchLocationsForNewBars = useCallback(
		async (bars: Record<string, number>) => {
			if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
			fetchTimeoutRef.current = setTimeout(async () => {
				if (!selectedMaterialLength?.id || !mainBarId) return;
				const entries = Object.entries(bars).filter(([, v]) => v > 0 && Number.isFinite(Number(v)));
				if (entries.length === 0) {
					setNewBarLocationOptionsMap({});
					return;
				}
				const distinctLengths = [...new Set(entries.map(([, v]) => Number(v).toFixed(3)))];
				setNewBarLocationsLoading(true);
				try {
					const newMap: Record<string, LocationOption[]> = {};
					for (const lengthKey of distinctLengths) {
						const len = Number(lengthKey);
						const params = {
							material_length_ids: [mainBarId],
							length_overrides: JSON.stringify({ [mainBarId]: len }),
							...(buildingId && { building: buildingId }),
						};
						const result = await fetchInventoryLocations(params).unwrap();
						const entry = (result?.by_material_length ?? []).find(
							(e: any) => String(e.material_length_id) === mainBarId
						);
						const options: LocationOption[] = [];
						if (entry?.locations) {
							const consumption = entry.consumption
								? {
									weight_per_bar: toNum(entry.consumption.weight_per_bar),
									volume_per_bar: toNum(entry.consumption.volume_per_bar),
									width_per_bar: toNum(entry.consumption.width_per_bar),
									height_per_bar: toNum(entry.consumption.height_per_bar),
								}
								: { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
						(entry.locations ?? []).forEach((loc: any) => {
							if (loc == null) return;
							const locId = String(loc.id);
							const effectiveAvailable = {
								weight: toNum(loc.available_weight),
								volume: toNum(loc.available_volume),
								width: toNum(loc.available_width),
								height: toNum(loc.available_height),
								bars: toNum(loc.available_bars),
							};
							const fits = barFitsInLocation(loc, consumption, effectiveAvailable);
							const noAvailability = hasNoAvailability(loc.type ?? '', effectiveAvailable, loc);
							options.push({
								value: locId,
								label: loc?.display_name || loc?.code || locId,
								disabled: !fits || noAvailability,
							});
						});
						}
						newMap[lengthKey] = options;
					}
					setNewBarLocationOptionsMap((prev) => ({ ...prev, ...newMap }));
				} catch (err) {
					console.error('Failed to fetch location candidates:', err);
				} finally {
					setNewBarLocationsLoading(false);
				}
			}, DEBOUNCE_MS);
		},
		[selectedMaterialLength?.id, mainBarId, fetchInventoryLocations]
	);

	const getNewBarLocationOptions = (index: number): LocationOption[] => {
		const bars = form.getFieldValue('new_length_bars') || [];
		const item = bars[index];
		const length = item?.length != null ? Number(item.length) : null;
		const lengthKey = length != null && Number.isFinite(length) ? length.toFixed(3) : '';
		const fetched = newBarLocationOptionsMap[lengthKey];
		if (!fetched) return mainLocationOptions;
		return fetched.map((option) => ({
			...option,
			label: formatLocation(option.label),
			searchText: locationSearchText(option.label, formatLocation),
		}));
	};
	const [splitMaterialLength, { isLoading: isSplitting }] = useSplitMaterialLengthMutation();
	const [difference, setDifference] = useState<number>(0);
	const [unallocated, setUnallocated] = useState<number>(0);

	const [selectedAssociatedJob, setSelectedAssociatedJob] = useState<Lineitem | null>(null);
	const [selectedCostType, setSelectedCostType] = useState<string | null>(null);
	const [showErrors, setShowErrors] = useState(false);
	const [locationWarning, setLocationWarning] = useState<string | null>(null);
	/** After Confirm on form fields, ask for Requested By PIN before saving. */
	const [operatorPinOpen, setOperatorPinOpen] = useState(false);

	// Material Location Summary modal after split
	const [showLocationSummary, setShowLocationSummary] = useState(false);
	const [locationSummaryData, setLocationSummaryData] = useState<{
		materialInfo: { type: string; width?: number; height?: number; shape?: string; outer_diameter?: number; inner_diameter?: number; wall_thickness?: number };
		lengths: Array<{ id: string; friendlyId: string; location: string; length: number; isScrapped?: boolean; overflowMessage?: string }>;
	}>({ materialInfo: { type: 'N/A' }, lengths: [] });

	// Initialize form with one default split option and check for associated job
	useEffect(() => {
		if (selectedMaterialLength && isSplitLengthModalOpen) {
			// Check for existing reservation to pre-fill associated job
			let defaultJob = null;
			if (selectedMaterialLength.material_reservations?.length > 0) {
				const res = selectedMaterialLength.material_reservations[0];
				if (res.order_line_item) {
					defaultJob = res.order_line_item as unknown as Lineitem;
					setSelectedAssociatedJob(defaultJob);
				}
			}

			const mainLength = parseFloat(selectedMaterialLength.current_length);
			form.setFieldsValue({
				new_lengths: [],
				new_length_bars: [],
				unallocated_length: 0,
				associated_job: defaultJob?.id,
				main_new_length: mainLength,
				main_location: selectedMaterialLength.inventory_location?.id ?? null,
			});
			setNewBarLocationOptionsMap({});
			setOperatorPinOpen(false);
			calculateDifference();
		}
	}, [selectedMaterialLength, isSplitLengthModalOpen]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
		};
	}, []);

	const calculateDifference = () => {
		const currentLength = parseFloat(selectedMaterialLength?.current_length || '0');
		const newLengths = form.getFieldValue('new_length_bars') || [];

		// Let's grab the value from the "New Length" field
		const mainNewLength = form.getFieldValue('main_new_length');

		// And the values from "New Length Bars" list
		const totalNewBarsLength = newLengths.reduce((sum: number, item: any) => {
			return sum + (item?.length || 0);
		}, 0);

		const totalNewLength = (mainNewLength || 0) + totalNewBarsLength;

		// Unallocated: Original - (MainNew + SumSplits)
		const unalloc = currentLength - totalNewLength;

		// When Unallocated Length turns from negative to positive, clear Attribute cost (options change)
		const wasNegative = unallocated < -0.001;
		const isNowPositive = unalloc > 0.001;
		if (wasNegative && isNowPositive) {
			setSelectedCostType(null);
			setSelectedAssociatedJob(null);
			form.setFieldsValue({ attribute_cost: undefined, associated_job: undefined });
		}

		setUnallocated(unalloc);

		// Difference: Original - MainNew
		const diff = currentLength - (mainNewLength || 0);
		setDifference(diff);
	};

	const mainNewLength = form.getFieldValue('main_new_length') || 0;
	// Minimum Required Length = ∑(Original Reservation Amount − Already Cut Lengths)
	const remainingReservedLength = (selectedMaterialLength?.material_reservations || []).reduce(
		(sum, res) => {
			if (res.material_cut_status === 'Cut') return sum;
			const reserved = Number(res.reserved_length || 0);
			const cutPerPiece = Number(res.material_cut_length || 0) + Number(res.saw_kerf || 0);
			const alreadyCut = cutPerPiece * (res.actual_cut_quantity || 0);
			return sum + Math.max(0, reserved - alreadyCut);
		},
		0
	);
	// Warn only when New Length is strictly below minimum required (use epsilon so >= does not show warning)
	const LENGTH_EPSILON = 0.001;
	const isConflict = Number(mainNewLength) < remainingReservedLength - LENGTH_EPSILON;


	const handleValuesChange = (changedValues: any, _allValues: any) => {
		calculateDifference();
		setLocationWarning(null);

		// main_location: keep current selection; effect will clear if not in new candidates (same as ModifyLengthsModal)

		if (Array.isArray(changedValues.new_length_bars)) {
			const bars = form.getFieldValue('new_length_bars') || [];
			let updated = false;
			const newBars = bars.map((item: any, i: number) => {
				const changed = changedValues.new_length_bars[i];
				if (changed && changed.length !== undefined && changed.location === undefined) {
					updated = true;
					return { ...item, location: undefined };
				}
				return item;
			});
			if (updated) {
				form.setFieldsValue({ new_length_bars: newBars });
			}

			// Fetch locations for new bars based on their lengths
			const newBarLengths: Record<string, number> = {};
			bars.forEach((item: any, i: number) => {
				if (item?.length > 0) newBarLengths[String(i)] = item.length;
			});
			fetchLocationsForNewBars(newBarLengths);
		}
	};

	// Check if unallocated is effectively zero
	const isUnallocatedZero = Math.abs(unallocated) < 0.001;
	const showJobSelector = selectedCostType && (selectedCostType.includes('Job Related'));

	/** Step 1: validate split form, then ask for Requested By PIN. */
	const handleOk = () => {
		form
			.validateFields()
			.then((values) => {
				setShowErrors(true);
				const newBars = values.new_length_bars || [];
				if (newBars.length === 0) {
					message.error('Please add at least one new length before splitting');
					return;
				}

				// Allow exactly 0 (original bar fully consumed); only reject null/negative.
				if (values.main_new_length == null || Number(values.main_new_length) < 0) {
					message.error('Original bar length cannot be negative');
					return;
				}

				const invalidBar = newBars.find((bar: any) => bar.length == null || Number(bar.length) <= 0);
				if (invalidBar) {
					message.error('All new bar lengths must be greater than 0');
					return;
				}

				if (!isUnallocatedZero && !selectedCostType) {
					message.error('Attribute Cost is required when there is an unallocated length difference');
					return;
				}

				if (showJobSelector && !selectedAssociatedJob) {
					message.error('Associated Job is required for Job Related costs');
					return;
				}

				setOperatorPinOpen(true);
			})
			.catch((info) => {
				console.error('Validate Failed:', info);
			});
	};

	/** Step 2: PIN resolved — run the split and show location summary. */
	const handleOperatorConfirm = async (userId: number, _pin: string) => {
		if (isSplitting) return;
		const values = form.getFieldsValue(true);
		const payload = {
			main_new_length: values.main_new_length,
			main_location: values.main_location === NO_LOCATION_VALUE ? null : values.main_location,
			unallocated_length: Number(unallocated.toFixed(4)),
			cost_attribute: selectedCostType,
			associated_job: selectedAssociatedJob?.id,
			user: userId,
			new_lengths: (values.new_length_bars || []).map((item: any) => ({
				length: item.length,
				quantity: item.quantity || 1,
				location_id: item.location,
			})),
		};

		try {
			const response = await splitMaterialLength({ id: selectedMaterialLength?.id, values: payload }).unwrap();
			message.success('Material split successfully');
			const createdLengths = response?.material_lengths ?? [];
			const refreshedOriginal = response?.original_length;

			const mainConsumed = Number(values.main_new_length) === 0;
			const originalBar = {
				id: refreshedOriginal?.id ?? selectedMaterialLength?.id ?? '',
				friendlyId: refreshedOriginal?.friendly_id ?? selectedMaterialLength?.friendly_id ?? '',
				location: mainConsumed
					? NO_LOCATION_LABEL
					: (refreshedOriginal?.inventory_location?.display_name ?? 'Auto Assign'),
				length: Number(refreshedOriginal?.current_length ?? values.main_new_length ?? 0),
				isScrapped: mainConsumed,
				overflowMessage: refreshedOriginal?.overflow_message || undefined,
			};

			const summaryData = {
				materialInfo: {
					type: material?.type?.display_name ?? 'N/A',
					width: material?.width != null ? Number(material.width) : undefined,
					height: material?.height != null ? Number(material.height) : undefined,
					outer_diameter: material?.outer_diameter != null ? Number(material.outer_diameter) : undefined,
					inner_diameter: material?.inner_diameter != null ? Number(material.inner_diameter) : undefined,
					wall_thickness: material?.wall_thickness != null ? Number(material.wall_thickness) : undefined,
					shape: material?.shape?.display_name,
				},
				lengths: [
					originalBar,
					...createdLengths.map((length: { id: string; friendly_id: string; inventory_location?: { display_name?: string }; current_length?: number; overflow_message?: string }) => ({
						id: length.id,
						friendlyId: length.friendly_id ?? '',
						location: length.inventory_location?.display_name ?? 'Unknown',
						length: Number(length.current_length ?? 0),
						isScrapped: false,
						overflowMessage: length.overflow_message || undefined,
					})),
				],
			};
			setOperatorPinOpen(false);
			setLocationSummaryData(summaryData);
			setShowLocationSummary(true);
		} catch (err) {
			console.error('Split error:', err);
			message.error('Failed to split material');
		}
	};

	const handleApplyToAll = (index: number) => {
		const list = form.getFieldValue('new_length_bars');
		const sourceRow = list[index];
		if (sourceRow) {
			// Apply length to all rows below, then validate location per row
			const invalidLocationRows: number[] = [];
			const updatedList = list.map((item: any, i: number) => {
				if (i <= index) return item;
				const newLength = sourceRow.length !== undefined ? sourceRow.length : item?.length;
				let newLocation = sourceRow.location !== undefined ? sourceRow.location : item?.location;

				// If applying a specific location, check if it's valid for this row's length
				// Skip validation when the target row has the same length as the source
				// (same length = same valid locations, and the options map may not be populated yet)
				if (newLocation && Number(newLength) !== Number(sourceRow.length)) {
					const rowOptions = getNewBarLocationOptions(i);
					if (rowOptions.length > 0 && !rowOptions.some((o) => o.value === newLocation)) {
						// Location not valid for this row's length — reset to auto-assign
						newLocation = undefined;
						invalidLocationRows.push(i + 1);
					}
				}

				return { ...item, length: newLength, location: newLocation };
			});
			form.setFieldsValue({ new_length_bars: updatedList });
			setTimeout(calculateDifference, 0);

			// If length was applied to all, refetch locations for affected rows
			if (sourceRow.length !== undefined) {
				const newBarLengths: Record<string, number> = {};
				updatedList.forEach((item: any, i: number) => {
					if (item?.length > 0) newBarLengths[String(i)] = item.length;
				});
				fetchLocationsForNewBars(newBarLengths);
			}

			// Build warning message
			if (invalidLocationRows.length > 0) {
				const sourceOptions = getNewBarLocationOptions(index);
				const locationLabel = sourceOptions.find((o) => o.value === sourceRow.location)?.label || 'selected location';
				setLocationWarning(
					`"${locationLabel}" is not a valid location for row(s) ${invalidLocationRows.join(', ')} due to different length. Those bars will be auto-assigned.`
				);
			} else if (sourceRow.location) {
				const sourceOptions = getNewBarLocationOptions(index);
				const locationLabel = sourceOptions.find((o) => o.value === sourceRow.location)?.label || 'selected location';
				const barsWithSameLocation = updatedList.filter((item: any) => item?.location === sourceRow.location).length;
				const mainLocation = form.getFieldValue('main_location');
				const totalBars = barsWithSameLocation + (mainLocation === sourceRow.location ? 1 : 0);
				if (totalBars > 1) {
					setLocationWarning(
						`${totalBars} bars assigned to "${locationLabel}". If the location doesn't have enough capacity, remaining bars will be auto-assigned.`
					);
				} else {
					setLocationWarning(null);
				}
			} else {
				setLocationWarning(null);
			}
		}
	};

	const handleDuplicate = (index: number, add: (val: any) => void) => {
		const list = form.getFieldValue('new_length_bars');
		const itemToDuplicate = list[index];
		add({ ...itemToDuplicate, location: undefined });
		setTimeout(() => {
			calculateDifference();
			const bars = form.getFieldValue('new_length_bars') || [];
			const newBarLengths: Record<string, number> = {};
			bars.forEach((item: any, i: number) => {
				if (item?.length > 0) newBarLengths[String(i)] = item.length;
			});
			fetchLocationsForNewBars(newBarLengths);
		}, 0);
	};

	if (!selectedMaterialLength) return null;

	// Get options based on unallocated value (Increase or Decrease)
	const getCostAttributeOptions = () => {
		let options = [];
		// unallocated < 0 means we have assigned MORE length than available (Increase)
		if (unallocated < -0.001) {
			options = [
				{ value: 'Error Correction', label: 'Error Correction' },
				{ value: 'Fastlane Job Related', label: 'Fastlane Job Related' },
				{ value: 'Swiss Job Related', label: 'Swiss Job Related' },
			];
		} else {
			// unallocated >= 0 means we have leftover length (Reduction/Usage)
			options = [
				{ value: 'Fastlane Program Related', label: 'Fastlane Program Related' },
				{ value: 'Fastlane Job Related', label: 'Fastlane Job Related' },
				{ value: 'Swiss Program Related', label: 'Swiss Program Related' },
				{ value: 'Swiss Job Related', label: 'Swiss Job Related' },
				{ value: 'Scrap Metal', label: 'Scrap Metal' },
				{ value: 'Thrown Out', label: 'Thrown Out' },
				{ value: 'Missing/Error', label: 'Missing/Error' },
			];
		}

		return options;
	};


	return (
		<>
			<Modal
				open={isSplitLengthModalOpen}
				title={
				<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<Title level={3} style={{ marginBottom: 0 }}>Split Material Length</Title>
					<BuildingTag name={buildingName} />
				</span>
			}
				width={1000}
				centered
				maskClosable={false}
				keyboard={false}
				onCancel={() => {
					if (showLocationSummary) return;
					setOperatorPinOpen(false);
					onClose();
				}}
				footer={[
					<Button
						key="cancel"
						onClick={() => {
							setOperatorPinOpen(false);
							onClose();
						}}
						size="large"
						style={{ minWidth: '100px' }}
					>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						onClick={handleOk}
						size="large"
						style={{ minWidth: '100px' }}
						loading={isSplitting}
					>
						Confirm
					</Button>,
				]}
				styles={{ body: { padding: '1.5rem' } }}
			>
				<Card style={{ marginBottom: '1.5rem', background: '#f5f5f5' }} size="small" bordered={false}>
					<Row gutter={[24, 16]} justify="space-between" align="middle">
						<Col>
							<Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Material</Text>
							<Text strong style={{ fontSize: '16px' }}>{material.display_name}</Text>
						</Col>
						<Col>
							<Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Dimensions</Text>
							<Text strong>{(() => {
								const w = material.width != null ? parseFloat(material.width) : NaN;
								const h = material.height != null ? parseFloat(material.height) : NaN;
								const od = (material as any).outer_diameter != null ? parseFloat((material as any).outer_diameter) : NaN;
								const id_ = (material as any).inner_diameter != null ? parseFloat((material as any).inner_diameter) : NaN;
								if (!isNaN(od)) {
									return !isNaN(id_)
										? `OD ${od.toFixed(3)}" / ID ${id_.toFixed(3)}"`
										: `OD ${od.toFixed(3)}"`;
								}
								if (!isNaN(w) && !isNaN(h)) {
									return `${w.toFixed(3)}" x ${h.toFixed(3)}"`;
								}
								return material.display_name ?? '—';
							})()}</Text>
						</Col>
						<Col>
							<Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Original Length</Text>
							<Text strong>{parseFloat(selectedMaterialLength.current_length).toFixed(3)}"</Text>
						</Col>
						<Col>
							<Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Difference</Text>
							<Text
								strong
								style={{
									fontSize: '18px',
									color: difference < 0 ? '#cf1322' : difference > 0 ? '#faad14' : '#52c41a'
								}}
							>
								{difference.toFixed(3)}"
							</Text>
						</Col>
					</Row>
				</Card>

				{isConflict && (
					<Card style={{ marginBottom: '1.5rem', background: '#fff1f0', borderColor: '#ffa39e' }} size="small">
						<div style={{ display: 'flex', gap: '8px', alignItems: 'center', color: '#cf1322' }}>
							<DeleteOutlined /> {/* Using DeleteOutlined just as an icon, or could use WarningOutlined if imported */}
							<Text strong type="danger">
								Warning: New Length ({mainNewLength}") is less than Remaining Reserved ({remainingReservedLength.toFixed(3)}").
							</Text>
						</div>
						<Text type="danger" style={{ fontSize: '13px' }}>
							Proceeding will result in a negative Available Length for this bar.
						</Text>
					</Card>
				)}

				<Form
					form={form}
					layout="vertical"
					onValuesChange={handleValuesChange}
					initialValues={{
						// Initial values are set in useEffect
					}}
				>
					{/* Main Length Section */}
					<Card
						title="Original Modified Bar"
						size="small"
						style={{ marginBottom: '1.5rem', borderColor: '#d9d9d9' }}
						headStyle={{ background: '#fafafa' }}
					>
						<Row gutter={16} align="middle">
							<Col span={4}>
								<Form.Item label="ID" style={{ marginBottom: 0 }}>
									<Tag color="blue" style={{ fontSize: '14px', padding: '4px 10px' }}>
										{selectedMaterialLength.friendly_id}
									</Tag>
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									label="New Length"
									name="main_new_length"
									rules={[{ required: true, message: 'Required' }]}
									style={{ marginBottom: 0 }}
								>
									<InputNumber
										style={{ width: '100%' }}
										suffix='"'
										size="large"
										min={0}
										max={240}
										precision={3}
									/>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Location"
									name="main_location"
									style={{ marginBottom: 0 }}
								>
									<Select
										showSearch
										filterOption={filterLocationOption}
										placeholder={
											!hasMainLengthChanged
												? 'Change new length to select location'
												: mainLocationsRequesting
													? 'Loading locations...'
													: 'Auto Assign'
										}
										allowClear
										options={
											!hasMainLengthChanged && mainBarCurrentLocIdStr
												? [{ value: mainBarCurrentLocIdStr, label: mainBarCurrentLocationLabel ?? mainBarCurrentLocIdStr }]
												: mainLocationOptions
										}
										optionRender={(opt) => renderLocationOption(opt.data as LocationOption)}
										size="large"
										loading={hasMainLengthChanged && mainLocationsRequesting}
										disabled={!hasMainLengthChanged || mainLocationsRequesting}
									/>
								</Form.Item>
							</Col>
						</Row>
					</Card>

					{/* Split Lengths Section */}
					<div style={{ marginBottom: '1rem' }}>
						<Row justify="space-between" align="middle" style={{ marginBottom: '1rem' }}>
							<Title level={5} style={{ margin: 0 }}>New Lengths</Title>
						</Row>

						{locationWarning && (
							<div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px', marginBottom: '1rem', color: '#ad6800' }}>
								{locationWarning}
							</div>
						)}

						<Form.List name="new_length_bars">
							{(fields, { add, remove }) => (
								<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
									{fields.map(({ key, name, ...restField }, index) => (
										<Row key={key} gutter={12} align="middle" style={{ background: '#fff', padding: '12px', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
											<Col span={6}>
												<Form.Item
													{...restField}
													name={[name, 'length']}
													style={{ marginBottom: 0 }}
													label={index === 0 ? "Length" : null}
													rules={[{ required: true, message: 'Req' }]}
												>
													<InputNumber placeholder='Length' style={{ width: '100%' }} suffix='"' min={0} max={240} precision={3} />
												</Form.Item>
											</Col>
											<Col span={6}>
												<Form.Item
													{...restField}
													name={[name, 'location']}
													style={{ marginBottom: 0 }}
													label={index === 0 ? "Location" : null}
												>
													<Select
														placeholder={newBarLocationsLoading ? "Loading locations..." : "Auto Assign"}
														allowClear
														showSearch
														filterOption={filterLocationOption}
														options={getNewBarLocationOptions(index)}
														loading={newBarLocationsLoading}
													/>
												</Form.Item>
											</Col>
											<Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
												<Form.Item label={index === 0 ? <span style={{ opacity: 0 }}>Action</span> : null} style={{ marginBottom: 0, width: '100%' }}>
													<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
														<Tooltip title="Apply length & location to all below">
															<Button icon={<VerticalAlignBottomOutlined />} onClick={() => handleApplyToAll(index)}>Apply to all</Button>
														</Tooltip>
														<Tooltip title="Duplicate this row">
															<Button icon={<CopyOutlined />} onClick={() => handleDuplicate(index, add)}>Duplicate</Button>
														</Tooltip>
														<Tooltip title="Remove">
															<Button danger icon={<DeleteOutlined />} onClick={() => {
															remove(name);
															setTimeout(() => {
																calculateDifference();
																const bars = form.getFieldValue('new_length_bars') || [];
																const newBarLengths: Record<string, number> = {};
																bars.forEach((item: any, i: number) => {
																	if (item?.length > 0) newBarLengths[String(i)] = item.length;
																});
																fetchLocationsForNewBars(newBarLengths);
															}, 0);
														}} />
														</Tooltip>
													</div>
												</Form.Item>
											</Col>
										</Row>
									))}
									<Button type="dashed" block onClick={() => add()} icon={<PlusOutlined />} style={{ height: '48px' }}>
										Add Length
									</Button>
								</div>
							)}
						</Form.List>
					</div>

					{/* Footer / Unallocated Section */}
					<div style={{ marginTop: '2rem', background: '#fafafa', padding: '1.5rem', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
						<Row gutter={24} align="middle">
							<Col span={6} style={{ textAlign: 'center', borderRight: '1px solid #e8e8e8' }}>
								<Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Unallocated Length</Text>
								<Text
									strong
									style={{
										fontSize: '20px',
										color: unallocated < 0 ? '#cf1322' : '#595959'
									}}
								>
									{unallocated.toFixed(3)}"
								</Text>
							</Col>
							<Col span={18}>
								<AttributeCostJobField
									options={getCostAttributeOptions()}
									attributeCost={selectedCostType}
									onAttributeCostChange={(val) => {
										setSelectedCostType(val);
										form.setFieldsValue({ attribute_cost: val ?? undefined });
										if (val) setShowErrors(false);
									}}
									associatedJob={selectedAssociatedJob}
									onAssociatedJobChange={(job) => {
										setSelectedAssociatedJob(job);
										form.setFieldsValue({ associated_job: job?.id ?? undefined });
										if (job) setShowErrors(false);
									}}
									disabled={isUnallocatedZero}
									materialReservations={selectedMaterialLength?.material_reservations}
									costRequired={!isUnallocatedZero}
									jobRequired={true}
									costError={showErrors && !isUnallocatedZero && !selectedCostType}
									jobError={Boolean(showErrors && showJobSelector && !selectedAssociatedJob)}
								/>
							</Col>
						</Row>
					</div>

				</Form>
			</Modal>

			<OperatorPinModal
				open={isSplitLengthModalOpen && operatorPinOpen && !showLocationSummary}
				title="Requested By"
				description="Enter the PIN of the person requesting this split."
				label="Requested By"
				expectedGroups={['Material Receivers']}
				onCancel={() => setOperatorPinOpen(false)}
				onConfirm={handleOperatorConfirm}
			/>

			{showLocationSummary && (
				<MaterialLocationsSummaryModal
					visible={showLocationSummary}
					onComplete={() => {
						setShowLocationSummary(false);
						refetchData();
						onClose();
					}}
					materialInfo={locationSummaryData.materialInfo}
					lengths={locationSummaryData.lengths}
					title="Material Locations Summary"
					source="receiving"
				/>
			)}
		</>
	);
};

export default SplitLengthModal;
